<template>
  <transition name="connection-banner">
    <div
      v-if="bannerVisible"
      :class="['connection-banner', `connection-banner--${bannerKind}`]"
      role="status"
      aria-live="polite"
    >
      <span
        :class="['mdi', bannerIconClass, 'connection-banner__icon']"
        aria-hidden="true"
      ></span>
      <span class="connection-banner__message">{{ bannerMessage }}</span>
      <button
        v-if="showRetry"
        type="button"
        class="connection-banner__retry"
        :disabled="isRetrying"
        @click="onRetryClick"
      >
        <span class="mdi mdi-refresh" aria-hidden="true"></span>
        {{ isRetrying ? 'กำลังลอง...' : 'ลองเชื่อมต่อใหม่' }}
      </button>
    </div>
  </transition>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "ConnectionBanner",
  data() {
    return {
      isRetrying: false,
    };
  },
  computed: {
    ...mapState({
      bannerVisible: (state) => state.connectionHealth.bannerVisible,
      bannerMessage: (state) => state.connectionHealth.bannerMessage,
      bannerKind: (state) => state.connectionHealth.bannerKind,
      backend: (state) => state.connectionHealth.backend,
    }),
    showRetry() {
      return this.backend === "down";
    },
    bannerIconClass() {
      if (this.bannerKind === "error") return "mdi-alert-circle-outline";
      if (this.bannerKind === "warning") return "mdi-sync";
      return "mdi-information-outline";
    },
  },
  methods: {
    async onRetryClick() {
      if (this.isRetrying) return;
      this.isRetrying = true;
      try {
        const result = await this.$store.dispatch("connectionHealth/manualRetry");
        if (result && result.shouldEscalate) {
          // Manual retries exhausted — route to the catastrophic page so
          // the user can either keep retrying or sit it out.
          this.$router.push({ name: "ConnectionError" }).catch(() => {});
        }
      } finally {
        this.isRetrying = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.connection-banner {
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9998; // below the network popup (9999) so both can coexist
  display: inline-flex;
  align-items: center;
  gap: 12px;
  max-width: calc(100vw - 32px);
  padding: 10px 18px;
  border-radius: 999px;
  background: #ffffff;
  color: #1f2937;
  font-family: "Noto Sans Thai", "Noto Sans", sans-serif;
  font-size: 14px;
  line-height: 1.4;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.12),
    0 10px 15px -3px rgba(0, 0, 0, 0.08);
  border: 1px solid transparent;
}

.connection-banner--warning {
  border-color: #f1c27d;
}

.connection-banner--error {
  border-color: #fca5a5;
}

.connection-banner__icon {
  font-size: 20px;
  line-height: 1;
}

.connection-banner--warning .connection-banner__icon {
  color: #ef6c00;
  animation: connection-banner-spin 1.6s linear infinite;
}

.connection-banner--error .connection-banner__icon {
  color: #d9101a;
}

.connection-banner__message {
  white-space: nowrap;
}

.connection-banner__retry {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid #d9101a;
  background: #ffffff;
  color: #d9101a;
  border-radius: 999px;
  font: inherit;
  font-size: 13px;
  padding: 4px 12px;
  cursor: pointer;
  transition: background-color 150ms ease, color 150ms ease;

  &:hover:not(:disabled) {
    background: #d9101a;
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.55;
    cursor: progress;
  }
}

@keyframes connection-banner-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.connection-banner-enter-active,
.connection-banner-leave-active {
  transition: opacity 220ms cubic-bezier(0.22, 1, 0.36, 1),
              transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}

.connection-banner-enter,
.connection-banner-leave-to {
  opacity: 0;
  transform: translate(-50%, -16px);
}

@media (prefers-reduced-motion: reduce) {
  .connection-banner-enter-active,
  .connection-banner-leave-active {
    transition: none;
  }
  .connection-banner--warning .connection-banner__icon {
    animation: none;
  }
}

@media (max-width: 480px) {
  .connection-banner {
    left: 16px;
    right: 16px;
    transform: none;
    max-width: none;
  }
  .connection-banner__message {
    white-space: normal;
  }
  .connection-banner-enter,
  .connection-banner-leave-to {
    transform: translateY(-16px);
  }
}
</style>
